/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { FunctionComponent, useEffect, useMemo, useState } from 'react';

import { Locale, LocaleKeys } from '../../../core/locale';
import { CursorManager, KeyManager } from '../../../core/navigation';
import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { ListTypes } from '../../../core/utils';
import { setListEntryFocus } from '../../../store/list/list.actions';
import { setPcHelpOptionsList } from '../../../store/settings/help/pchelp/pchelp.reducers';
import { List, Mousetrap, ScreenPreview } from '../../common';
import { SettingsTemplate } from '../SettingsTemplate';

import '../SettingsCommon.scss';

const ParentalControlHelp: FunctionComponent = (): JSX.Element => {
    const dispatch = useTivoDispatch();
    const [isCursorActive, setIsCursorActive] = useState(false);
    const { optionsList, selectedOption } = useTivoSelector((state: RootState) => state.pcHelp);

    const selectedOptionIndex = useMemo(() => {
        return optionsList.reduce(
            (acc, option, index) => (option.value === selectedOption?.value ? index : acc),
            0,
        );
    }, [optionsList, selectedOption]);

    const onCursorStateChangeCallback = (state) => {
        setIsCursorActive(state);
        if (!isCursorActive) dispatch(setListEntryFocus(ListTypes.PC_HELP, true));
    };

    useEffect(() => {
        dispatch(setPcHelpOptionsList());
        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);
        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }
        return () => {
            CursorManager.unsubscribe(refCursorManager);
        };
    }, []);

    const renderParentalHelpList = () => {
        return (
            <div className="settings-common-content">
                <List
                    type={ListTypes.PC_HELP}
                    itemList={optionsList}
                    initialFocusedIndex={selectedOptionIndex}
                    initialSelectedIndex={selectedOptionIndex}
                    generateItemView={(item: any, itemClass, index) => (
                        <Mousetrap
                            key={index}
                            value={`${index}_${ListTypes.PC_HELP}`}
                            className={`item ${itemClass}`}
                        >
                            <span>{item.value}</span>
                        </Mousetrap>
                    )}
                />
            </div>
        );
    };

    return (
        <SettingsTemplate
            title={Locale.getString(LocaleKeys.SETTINGS_PC_HEADER_TITLE_TEXT).toUpperCase()}
        >
            {renderParentalHelpList()}
            <ScreenPreview>
                <div>{selectedOption?.preview}</div>
            </ScreenPreview>
        </SettingsTemplate>
    );
};

export default ParentalControlHelp;
